import { z } from 'zod'

export const K8sConfig = z.object({
  namespace: z.string(),
  status: z.enum([
    'provisioning',
    'active',
    'failed',
    'deprovisioning',
    'deprovisioned',
    'suspended'
  ]),
  error_message: z.string().nullable().optional(),
  git_repo_url: z.string().nullable().optional(),
  git_path: z.string(),
  git_revision: z.string(),
  self_heal: z.boolean(),
  provisioned_at: z.number().nullable().optional(),
  billing_type: z.enum(['payg', 'fixed', 'free']).default('fixed'),
  included_egress_gib: z.number().nullable().optional(),
  tier: z.enum(['free', 'paid']).default('paid'),
  had_free_tier: z.boolean().default(false),
  suspended_at: z.number().nullable().optional(),
  suspended_reason: z.string().nullable().optional(),
  egress_cap_gib_mo: z.number().nullable().optional()
})
export type K8sConfig = z.infer<typeof K8sConfig>

export const K8sStatus = z.object({
  namespace: z.string(),
  status: z.string(),
  sync_status: z.string().optional(),
  health_status: z.string().optional()
})
export type K8sStatus = z.infer<typeof K8sStatus>

export const K8sDomain = z.object({
  id: z.number(),
  domain: z.string(),
  verification_token: z.string(),
  verified: z.boolean(),
  auto_dns: z.boolean().default(false),
  verified_at: z.string().nullable().optional(),
  created_at: z.string()
})
export type K8sDomain = z.infer<typeof K8sDomain>

// ---------- Free tier ----------
//
// Mirrors backend/docs/specs/schemas/k8s.yaml additions for the perpetual
// free K8s namespace claim/release/resume/upgrade endpoints.

export const K8sFreeClaimRequest = z.object({
  turnstile_token: z.string()
})
export type K8sFreeClaimRequest = z.infer<typeof K8sFreeClaimRequest>

export const K8sFreeClaimResponse = z.object({
  namespace: z.string(),
  parent_domain: z.string(),
  kubeconfig_url: z.string().optional(),
  registry_endpoint: z.string().nullable().optional()
})
export type K8sFreeClaimResponse = z.infer<typeof K8sFreeClaimResponse>

export const K8sFreeUpgradeRequest = z.object({
  target_product_slug: z.enum([
    'namespace-s',
    'namespace-m',
    'namespace-l',
    'namespace-payg'
  ]),
  // Required only for fixed-tier targets (Phase 2). The PAYG path uses the
  // account's existing Stripe customer + payment-method setup.
  payment_method_id: z.string().optional(),
  // The consent to start the paid tier inside its withdrawal period, the
  // legal text version every switch is sent with, and the acceptance of the
  // current terms while the account has terms to accept.
  withdrawal_consent: z.boolean().optional(),
  consent_version: z.string().optional(),
  terms_accepted: z.boolean().optional()
})
export type K8sFreeUpgradeRequest = z.infer<typeof K8sFreeUpgradeRequest>

export const K8sFreeUpgradeResponse = z.object({
  namespace: z.string(),
  tier: z.literal('paid'),
  target_product_slug: z.string(),
  stripe_subscription_id: z.string().optional()
})
export type K8sFreeUpgradeResponse = z.infer<typeof K8sFreeUpgradeResponse>

export const K8sFreeResumeResponse = z.object({
  namespace: z.string(),
  status: z.string()
})
export type K8sFreeResumeResponse = z.infer<typeof K8sFreeResumeResponse>
